import React, { useCallback, useContext, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, UseFormSetError } from "react-hook-form";
import * as z from "zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "@/components/ui/use-toast";
import { useApiErrorHandler } from "@/lib/errorHandler";
import { AuthContext, AuthContextType } from "@/context/AuthContext";
import { zodKeys } from "@/lib/utils";
import { User, userApi, UserGroup, userGroupApi } from "@/api/users";
import { useListApi } from "@/hooks/useListApi";
import { DynamicSelectorV2 } from "../DynamicSelectorV2";
import { useTranslation } from "react-i18next";
import { useOptimisticLocking } from "@/hooks/useOptimisticLocking";
import { ConflictResolutionDialog } from "@/components/ui/conflict-resolution-dialog";
import {
  withVersion,
  createOptimisticLockingToasts,
} from "@/lib/optimisticLockingHelpers";
import { userFieldConfig } from "@/lib/fieldDiffUtils";

function generatePassword() {
  const length = 16,
    charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let retVal = "";
  let i = 0;
  for (; i < length; ++i) {
    retVal += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return retVal;
}

export function AddUpdateUser({
  open,
  setOpen,
  userObject,
  trigger,
  refresh,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  userObject?: User;
  trigger?: JSX.Element;
  refresh?: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { values: contextValues, set } = useContext(
    AuthContext,
  ) as AuthContextType;

  const { loading, user } = contextValues;
  const { setLoading } = set;

  const toasts = createOptimisticLockingToasts(t("users.title.user"), t);

  const FormSchema = z.object({
    username: z.string().min(5, {
      message: t("users.statictext.username_required_with_description"),
    }),
    email: z
      .string()
      .min(1, { message: t("common.statictext.field_required") })
      .email(t("common.statictext.field_required")),
    first_name: z.string().min(2, {
      message: t("common.statictext.first_name_too_short"),
    }),
    last_name: z.string().min(2, {
      message: t("common.statictext.last_name_too_short"),
    }),
    group: z.string().min(1, {
      message: t("users.statictext.select_group"),
    }),
    version: z.number().optional(), // For optimistic locking
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      username: "",
      email: "",
      first_name: "",
      last_name: "",
      group: "",
    },
  });

  // Get current form data for field diff comparison
  const getCurrentFormData = useCallback(
    (version: number): User => {
      const formData = form.getValues();
      return {
        id: userObject?.id,
        version: version,
        username: formData.username,
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        group: formData.group,
      } as User;
    },
    [form, userObject?.id],
  );

  // Optimistic locking with full state management
  const {
    currentVersion,
    updateVersion,
    updateWithLocking,
    conflictState,
    handleRefresh,
    handleForceOverwrite,
    clearConflict,
  } = useOptimisticLocking<User>({
    initialVersion: userObject?.version || 0,
    resourceId: userObject?.id || 0,
    resourceName: t("users.title.user"),
    apiRead: (id) =>
      userApi.read({
        id: id.toString(),
        token: user?.access,
      }) as Promise<any>,
    apiUpdate: (id, data) =>
      userApi.update({
        id: id.toString() + "/",
        data,
        token: user?.access,
      }),
    onUpdateSuccess: (data) => {
      toast(toasts.updateSuccess);
    },
    onConflict: () => {
      toast(toasts.conflictDetected);
    },
    onUpdateError: (error) => {
      toast(toasts.error(error));
    },
    diffConfig: userFieldConfig,
    localData: userObject ? getCurrentFormData(userObject.version) : undefined,
  });

  const errorHandler = useApiErrorHandler("AddUpdateUser", {
    setFormError: form.setError as UseFormSetError<Record<string, unknown>>,
  });

  const {
    loading: userGroupLoading,
    list: userGroupsList,
    apiResult: userGroupsResult,
    search,
    loadNext,
    pagination,
  } = useListApi<UserGroup>({ baseApiObject: userGroupApi, enable: open });

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    try {
      setLoading?.(true);
      if (userObject && userObject.id) {
        // Use optimistic locking for updates
        const updateData = withVersion(
          {
            username: data.username.toLowerCase(),
            email: data.email,
            first_name: data.first_name,
            last_name: data.last_name,
            group: data.group,
            updated_by: user?.user.id,
          },
          currentVersion,
        );

        const result = await updateWithLocking(updateData);

        // If there was a conflict or the update failed, don't close the dialog
        if (result.conflict || !result.success) return;

        await refresh?.();
        setOpen(false);
      } else {
        // Create new user (no optimistic locking needed)
        await userApi.create({
          url: "/",
          data: {
            username: data.username.toLowerCase(),
            email: data.email,
            first_name: data.first_name,
            last_name: data.last_name,
            group: data.group,
            created_by: user?.user.id,
            password: generatePassword(),
          },
          token: user?.access,
        });
        toast({
          title: t("common.toast.added_successfully", {
            resource: t("users.title.user"),
          }),
        });
        form.reset();
        await refresh?.();
        setOpen(false);
      }
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading?.(false);
    }
  }

  useEffect(() => {
    if (userObject) {
      zodKeys(FormSchema).map((field) => {
        form.setValue(
          field as keyof z.infer<typeof FormSchema>,
          {
            ...userObject,
            group:
              userObject.groups.find((item) => item.id)?.id.toString() || "",
            version: userObject?.version || 0,
          }[field as keyof z.infer<typeof FormSchema>],
          {
            shouldTouch: true,
          },
        );
        return undefined;
      });
      // Update version when userObject changes
      updateVersion(userObject?.version || 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userObject]);

  const buttonAction = userObject?.id
    ? t("common.button.update")
    : t("common.button.add");

  // Conflict resolution handlers
  const handleRefreshUser = async () => {
    if (!userObject?.id) return;
    const refreshed = await handleRefresh();
    if (refreshed) {
      // Update form with refreshed data
      updateVersion(refreshed.version);
      zodKeys(FormSchema).map((field) => {
        form.setValue(
          field as keyof z.infer<typeof FormSchema>,
          {
            ...refreshed,
            group:
              refreshed.groups.find((item) => item.id)?.id.toString() || "",
            version: refreshed?.version || 0,
          }[field as keyof z.infer<typeof FormSchema>],
          {
            shouldTouch: true,
          },
        );
        return undefined;
      });
      toast(toasts.refreshSuccess);
    }
  };

  const handleForceOverwriteUser = async () => {
    if (!conflictState.latestVersion || !userObject?.id) return;
    const formData = form.getValues();
    const result = await handleForceOverwrite(
      withVersion(
        {
          username: formData.username.toLowerCase(),
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          group: formData.group,
          updated_by: user?.user.id,
        },
        conflictState.latestVersion,
      ),
    );
    if (result.success) {
      toast(toasts.overwriteSuccess);
      await refresh?.();
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!!trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent
        className="w-full md:w-[500px] max-h-screen overflow-y-auto"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="w-full flex flex-col gap-8"
          >
            <DialogHeader>
              <DialogTitle>
                {buttonAction} {t("users.title.user")}
              </DialogTitle>
            </DialogHeader>
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>* {t("common.label.username")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("common.placeholder.username")}
                      {...field}
                      disabled={!!userObject?.id}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>* {t("common.label.email")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("common.placeholder.email")}
                      {...field}
                      disabled={!!userObject?.id}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="first_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>* {t("common.label.first_name")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("common.placeholder.first_name")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>* {t("common.label.last_name")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("common.placeholder.last_name")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="group"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    * {t("users.label.group")}{" "}
                    {userGroupLoading
                      ? t("common.button.loading_with_parens")
                      : ""}
                  </FormLabel>
                  <DynamicSelectorV2
                    onValueChange={field.onChange}
                    value={field.value || undefined}
                    options={userGroupsList.map((item) => ({
                      label: item.name,
                      value: item.id.toString(),
                    }))}
                    className="selector"
                    onLoadNext={loadNext}
                    onSearch={(searchValue) => {
                      search.set(searchValue);
                    }}
                    placeholder={t("users.placeholder.group")}
                    totalCount={userGroupsResult?.count || 0}
                    pageSize={pagination.pageSize}
                    currentPage={pagination.page}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button disabled={loading} type="submit">
                {loading ? t("common.button.loading") : buttonAction}
              </Button>
            </DialogFooter>
          </form>
        </Form>

        {/* Conflict Resolution Dialog */}
        <ConflictResolutionDialog
          open={conflictState.isOpen}
          onOpenChange={(open) => !open && clearConflict()}
          currentVersion={currentVersion}
          latestVersion={conflictState.latestVersion}
          resourceName={t("users.title.user")}
          onRefresh={async () => {
            await handleRefreshUser();
            await refresh?.();
          }}
          onOverwrite={async () => {
            await handleForceOverwriteUser();
            await refresh?.();
          }}
          loading={loading}
          fieldDiffs={conflictState.fieldDiffs}
          fieldLabels={Object.fromEntries(
            Object.entries(userFieldConfig.fields).map(([field, config]) => [
              field,
              t(config.label),
            ]),
          )}
        />
      </DialogContent>
    </Dialog>
  );
}
